/**
 * Removes citation marks from text content.
 * Removes <cite>...</cite> tags from the provided text.
 *
 * @param text - The text content that may contain citation marks
 * @returns The text with citation marks removed
 */
export const removeCitations = (text: string): string => {
  if (!text || typeof text !== "string") {
    return text || "";
  }

  try {
    // Remove raw <cite> tags (for unprocessed markdown)
    const citeRegex = /<cite>\s*([^<]*?)\s*<\/cite>/g;
    const result = text.replace(citeRegex, "");

    // Clean up multiple consecutive spaces and trim
    return result.replace(/\s+/g, " ").trim();
  } catch (error) {
    console.warn("Error removing citations from text:", error);
    return text;
  }
};

/**
 * Removes citation marks from a cloned DOM element while preserving rich formatting.
 * Removes citation badge elements from the DOM and cleans up whitespace.
 *
 * @param element - The DOM element to process
 * @returns The element with citations removed and whitespace cleaned up
 */
export const removeCitationsFromElement = (element: HTMLElement): HTMLElement => {
  if (!element || !element.querySelectorAll) {
    console.warn("Invalid element provided to removeCitationsFromElement");
    return element;
  }

  try {
    // Remove citation badge elements from the DOM (both clickable and non-clickable)
    const citationBadges = element.querySelectorAll(".citation-badge");
    citationBadges.forEach((badge) => {
      try {
        badge.remove();
      } catch (removeError) {
        console.warn("Error removing citation badge:", removeError);
      }
    });

    // Clean up whitespace in the HTML while preserving structure
    if (element.innerHTML) {
      element.innerHTML = element.innerHTML
        .replace(/\s+/g, " ") // Replace multiple whitespace with single space
        .replace(/>\s+</g, "><") // Remove spaces between HTML tags
        .replace(/\s+/g, " ") // Clean up any remaining multiple spaces
        .trim();
    }

    return element;
  } catch (error) {
    console.warn("Error removing citations from element:", error);
    return element;
  }
};
